import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getWorkouts,
  getExercises,
  createWorkout,
  deleteWorkout,
  getWorkoutById,
  patchWorkout,
  createExercise,
  getUserProfile,
  patchUserProfile,
  patchExercise,
  deleteExercise,
  mergeExercise,
  getWorkoutRoutines,
  getWorkoutRoutineById,
  createWorkoutRoutine,
  deleteWorkoutRoutine,
  patchWorkoutRoutine,
  acceptFriendRequest,
  declineFriendRequest,
  getFriends,
  getFriendRequests,
  createFriendRequest,
  deleteFriend,
  getOutgoingFriendRequests,
  deleteFriendRequest,
  shareWorkoutRoutine,
  getSharedWorkoutRoutines,
  getExerciseById,
  importWorkoutRoutine,
  deleteSharedWorkoutRoutine,
  patchSharedWorkoutRoutine,
  getActivityFeed,
  likeActivityFeedItem,
  unlikeActivityFeedItem,
  completeWorkout,
} from "./client.ts";
import {
  CreateWorkoutRequest,
  PatchWorkoutRequest,
  CreateExerciseRequest,
  User,
  CreateWorkoutRoutineRequest,
  PatchWorkoutRoutineRequest,
  PatchExerciseRequest,
  CreateFriendRequest,
  ShareWorkoutRoutineRequest,
  PatchSharedWorkoutRoutineRequest,
} from "./types.ts";
import { useLocation } from "react-router-dom";

// Query keys
export const queryKeys = {
  workouts: ["workouts"] as const,
  workout: (id: string) => ["workout", id] as const,
  workoutRoutines: ["workout-routines"] as const,
  sharedWorkoutRoutines: ["shared-workout-routines"] as const,
  sharedWorkoutRoutine: (id: string) => ["shared-workout-routine", id] as const,
  sharedExercises: ["shared-exercises"] as const,
  workoutRoutine: (id: string) => ["workout-routine", id] as const,
  exercises: ["exercises"] as const,
  exercise: (id: string) => ["exercise", id] as const,
  profile: ["profile"] as const,
  friends: ["friends"] as const,
  friendRequests: ["friend-requests"] as const,
  outgoingFriendRequests: ["outgoing-friend-requests"] as const,
  activityFeed: ["activity-feed"] as const,
  activityFeedItem: (id: string) => ["activity-feed", id] as const,
};

// User hooks
export function useProfile(enabled: boolean = true) {
  return useQuery({
    queryKey: queryKeys.profile,
    queryFn: getUserProfile,
    retry: 2,
    retryDelay: 1000,
    enabled,
  });
}

export function useFriends() {
  return useQuery({
    queryKey: queryKeys.friends,
    queryFn: getFriends,
    retry: 2,
    retryDelay: 1000,
  });
}

export function useFriendRequests() {
  return useQuery({
    queryKey: queryKeys.friendRequests,
    queryFn: getFriendRequests,
    refetchInterval: 60000,
    retry: 2,
    retryDelay: 1000,
  });
}

export function useOutgoingFriendRequests() {
  return useQuery({
    queryKey: queryKeys.outgoingFriendRequests,
    queryFn: getOutgoingFriendRequests,
    refetchInterval: 60000,
    retry: 2,
    retryDelay: 1000,
  });
}

export function useCreateFriendRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: CreateFriendRequest) => createFriendRequest(request),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.outgoingFriendRequests,
      });
    },
  });
}

export function useCancelFriendRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteFriendRequest(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.outgoingFriendRequests,
      });
    },
  });
}

export function useAcceptFriendRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => acceptFriendRequest(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.friends });
      queryClient.invalidateQueries({ queryKey: queryKeys.friendRequests });
    },
  });
}

export function useDeclineFriendRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => declineFriendRequest(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.friendRequests });
    },
  });
}

export function useDeleteFriend() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteFriend(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.friends });
    },
  });
}

export function usePatchProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (profileData: Partial<User>) => patchUserProfile(profileData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.profile });
    },
  });
}

// Activity feed hooks

export function useActivityFeed(
  from?: Date,
  to?: Date,
  page?: number,
  pageSize?: number,
) {
  const location = useLocation();

  return useQuery({
    queryKey: [
      ...queryKeys.activityFeed,
      from?.toISOString(),
      to?.toISOString(),
      page,
      pageSize,
    ],
    queryFn: () => getActivityFeed(from, to, page, pageSize),
    enabled: !!from && !!to && location.pathname === "/activity",
    refetchInterval: 60000,
    retry: 2,
    retryDelay: 1000,
  });
}

export function useLikeActivityFeedItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (activityFeedItemId: string) =>
      likeActivityFeedItem(activityFeedItemId),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.activityFeed,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.activityFeedItem(id),
      });
    },
  });
}

export function useUnlikeActivityFeedItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (activityFeedItemId: string) =>
      unlikeActivityFeedItem(activityFeedItemId),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.activityFeed,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.activityFeedItem(id),
      });
    },
  });
}

// Workouts hooks
export function useWorkouts(
  from?: Date,
  to?: Date,
  page?: number,
  pageSize?: number,
  status?: string,
) {
  return useQuery({
    queryKey: [
      ...queryKeys.workouts,
      from?.toISOString(),
      to?.toISOString(),
      page,
      pageSize,
      status,
    ],
    queryFn: () => getWorkouts(from, to, page, pageSize, status),
    enabled: !!from && !!to,
  });
}

export function useWorkout(id: string) {
  return useQuery({
    queryKey: queryKeys.workout(id),
    queryFn: () => getWorkoutById(id),
    enabled: !!id,
  });
}

export function useCreateWorkout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (workout: CreateWorkoutRequest) => createWorkout(workout),
    onSuccess: () => {
      // Invalidate and refetch workouts list
      queryClient.invalidateQueries({ queryKey: queryKeys.workouts });
      queryClient.invalidateQueries({ queryKey: queryKeys.activityFeed });
    },
  });
}

export function useDeleteWorkout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteWorkout(id),
    onSuccess: () => {
      // Invalidate and refetch workouts list
      queryClient.invalidateQueries({ queryKey: queryKeys.workouts });
    },
  });
}

export function usePatchWorkout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      workout,
    }: {
      id: string;
      workout: Partial<PatchWorkoutRequest>;
    }) => patchWorkout(id, workout),
    onSuccess: (_, { id }) => {
      // Invalidate and refetch workouts list and specific workout
      queryClient.invalidateQueries({ queryKey: queryKeys.workouts });
      queryClient.invalidateQueries({ queryKey: queryKeys.workout(id) });
    },
  });
}

export function useCompleteWorkout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => completeWorkout(id),
    onSuccess: (_, id) => {
      // Invalidate and refetch workouts list and specific workout
      queryClient.invalidateQueries({ queryKey: queryKeys.workouts });
      queryClient.invalidateQueries({ queryKey: queryKeys.workout(id) });
    },
  });
}

// Exercises hooks
export function useExercises() {
  return useQuery({
    queryKey: queryKeys.exercises,
    queryFn: getExercises,
  });
}

export function useExercise(id: string) {
  return useQuery({
    queryKey: queryKeys.exercise(id),
    queryFn: () => getExerciseById(id),
    enabled: !!id,
  });
}

export function useCreateExercise() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (exercise: CreateExerciseRequest) => createExercise(exercise),
    onSuccess: () => {
      // Invalidate and refetch exercises list
      queryClient.invalidateQueries({ queryKey: queryKeys.exercises });
    },
  });
}

export function usePatchExercise() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      exercise,
    }: {
      id: string;
      exercise: Partial<PatchExerciseRequest>;
    }) => patchExercise(id, exercise),
    onSuccess: (_, { id }) => {
      // Invalidate and refetch exercises list
      queryClient.invalidateQueries({ queryKey: queryKeys.exercises });
      queryClient.invalidateQueries({ queryKey: queryKeys.exercise(id) });
    },
  });
}

export function useDeleteExercise() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteExercise(id),
    onSuccess: () => {
      // Invalidate and refetch exercises list
      queryClient.invalidateQueries({ queryKey: queryKeys.exercises });
    },
  });
}

export function useMergeExercise() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      targetExerciseId,
      sourceExerciseId,
    }: {
      targetExerciseId: string;
      sourceExerciseId: string;
    }) => mergeExercise(targetExerciseId, sourceExerciseId),
    onSuccess: () => {
      // Invalidate exercises, workouts, and routines since they might have changed
      queryClient.invalidateQueries({ queryKey: queryKeys.exercises });
      queryClient.invalidateQueries({ queryKey: queryKeys.workouts });
      queryClient.invalidateQueries({ queryKey: queryKeys.workoutRoutines });
    },
  });
}

// Workout Routines hooks
export function useWorkoutRoutines() {
  return useQuery({
    queryKey: queryKeys.workoutRoutines,
    queryFn: getWorkoutRoutines,
  });
}

// Workout Routines hooks
export function useSharedWorkoutRoutines() {
  const location = useLocation();

  return useQuery({
    queryKey: queryKeys.sharedWorkoutRoutines,
    queryFn: getSharedWorkoutRoutines,
    refetchInterval: 60000,
    retry: 2,
    retryDelay: 1000,
    enabled: location.pathname === "/routines",
  });
}

export function useSharedExercises() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: queryKeys.sharedExercises,
    mutationFn: (exerciseIds: string[]) => {
      return Promise.all(exerciseIds.map((id) => getExerciseById(id)));
    },
    onSuccess: () => {
      // Invalidate and refetch workout routines list
      queryClient.invalidateQueries({
        queryKey: queryKeys.sharedWorkoutRoutines,
      });
    },
  });
}

export function useImportWorkoutRoutine() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (routineId: string) => importWorkoutRoutine(routineId),
    onSuccess: () => {
      // Invalidate and refetch workout routines list
      queryClient.invalidateQueries({ queryKey: queryKeys.workoutRoutines });
      queryClient.invalidateQueries({ queryKey: queryKeys.exercises });
      queryClient.invalidateQueries({
        queryKey: queryKeys.sharedWorkoutRoutines,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.sharedExercises,
      });
    },
  });
}

export function useDeleteSharedWorkoutRoutine() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteSharedWorkoutRoutine(id),
    onSuccess: () => {
      // Invalidate and refetch workout routines list
      queryClient.invalidateQueries({
        queryKey: queryKeys.sharedWorkoutRoutines,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.sharedExercises,
      });
    },
  });
}

export function useWorkoutRoutine(id: string) {
  return useQuery({
    queryKey: queryKeys.workoutRoutine(id),
    queryFn: () => getWorkoutRoutineById(id),
    enabled: !!id,
  });
}

export function useCreateWorkoutRoutine() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (routine: CreateWorkoutRoutineRequest) =>
      createWorkoutRoutine(routine),
    onSuccess: () => {
      // Invalidate and refetch workout routines list
      queryClient.invalidateQueries({ queryKey: queryKeys.workoutRoutines });
    },
  });
}

export function useShareWorkoutRoutine() {
  return useMutation({
    mutationFn: (shareData: ShareWorkoutRoutineRequest) =>
      shareWorkoutRoutine(shareData.friendId, shareData.routineId),
  });
}

export function usePatchSharedWorkoutRoutine() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      routine,
    }: {
      id: string;
      routine: Partial<PatchSharedWorkoutRoutineRequest>;
    }) => patchSharedWorkoutRoutine(id, routine),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.sharedWorkoutRoutine(id),
      });
    },
  });
}

export function useDeleteWorkoutRoutine() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteWorkoutRoutine(id),
    onSuccess: () => {
      // Invalidate and refetch workout routines list
      queryClient.invalidateQueries({ queryKey: queryKeys.workoutRoutines });
    },
  });
}

export function usePatchWorkoutRoutine() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      routine,
    }: {
      id: string;
      routine: Partial<PatchWorkoutRoutineRequest>;
    }) => patchWorkoutRoutine(id, routine),
    onSuccess: (_, { id }) => {
      // Invalidate and refetch workout routines list and specific routine
      queryClient.invalidateQueries({ queryKey: queryKeys.workoutRoutines });
      queryClient.invalidateQueries({ queryKey: queryKeys.workoutRoutine(id) });
    },
  });
}
